/**
 * Ladeberegninger: hurtigladning (DC) og hjemmeladning (AC).
 * Bruges af bilsiden, sammenligning og senere jobs.
 */

export interface DcChargeInput {
  batteryKwh: number;
  /** Peak DC-effekt i kW */
  dcKw: number;
  /** Målt 10–80 %-tid, hvis den findes */
  measured1080Min?: number | null;
  consumptionWhKm?: number | null;
}

export interface DcChargeResult {
  minutes1080: number;
  /** Er tiden målt/oplyst eller beregnet? */
  isEstimated: boolean;
  /** km tilført pr. 10 min hurtigladning */
  kmPer10Min: number | null;
  /** Gennemsnitlig effekt i 10–80 %-vinduet */
  avgKw: number;
}

/**
 * Realistisk gennemsnit i 10–80 %-vinduet er typisk 60–70 % af peak,
 * fordi ladekurven falder over 50 % SoC.
 */
export const DC_CURVE_EFFICIENCY = 0.65;

export function estimateDcCharge({
  batteryKwh,
  dcKw,
  measured1080Min,
  consumptionWhKm,
}: DcChargeInput): DcChargeResult {
  const energy = batteryKwh * 0.7; // 10 → 80 %
  const avgKw = Math.max(1, dcKw * DC_CURVE_EFFICIENCY);
  const estimated = Math.round((energy / avgKw) * 60);
  const minutes1080 = measured1080Min && measured1080Min > 0 ? measured1080Min : estimated;

  const kmPer10Min =
    consumptionWhKm && consumptionWhKm > 0
      ? Math.round(((energy * (10 / minutes1080)) * 1000) / consumptionWhKm)
      : null;

  return {
    minutes1080,
    isEstimated: !(measured1080Min && measured1080Min > 0),
    kmPer10Min,
    avgKw: Math.round(avgKw),
  };
}

export interface AcChargeInput {
  batteryKwh: number;
  /** Ladeboksens effekt i kW (typisk 3,7 / 7,4 / 11 / 22) */
  boxKw: number;
  /** Bilens maks AC-effekt i kW */
  carAcKw?: number | null;
  fromPct?: number;
  toPct?: number;
}

export interface AcChargeResult {
  hours: number;
  effectiveKw: number;
  limitedByCar: boolean;
}

/** AC-tab i onboard-lader og kabler er typisk 8–10 %. */
export const AC_LOSS = 0.92;

export function estimateAcCharge({
  batteryKwh,
  boxKw,
  carAcKw,
  fromPct = 20,
  toPct = 100,
}: AcChargeInput): AcChargeResult {
  const carMax = carAcKw && carAcKw > 0 ? carAcKw : boxKw;
  const effectiveKw = Math.min(boxKw, carMax);
  const energy = batteryKwh * Math.max(0, toPct - fromPct) / 100;
  const hours = energy / (effectiveKw * AC_LOSS);
  return {
    hours: Math.round(hours * 10) / 10,
    effectiveKw,
    limitedByCar: carMax < boxKw,
  };
}
